import { useQueryClient } from '@tanstack/react-query';
import { useGetETSTicketsInfiniteQuery } from 'gcs-common/clients/api/entities/tickets/tickets-api';
import {
  type FetchETSTicketsResponseData,
  convertETSTicketsFiltersToQuery,
  getGetETSTicketsInfiniteQueryKey,
  useETSTicketsQueryParams,
} from 'gcs-common/clients/api/entities/tickets/tickets-api-helpers';
import { useGetETSTicketNotificationsForChannelSelector } from 'gcs-common/clients/api/entities/tickets/tickets-api-selectors';
import IconComponent from 'gcs-common/components/Icon/Icon';
import InfiniteScroller from 'gcs-common/components/InfiniteScroller/InfiniteScroller';
import { ICON, ICON_COLOR, ICON_SIZE } from 'gcs-common/constants/IconConstants';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import { createNewTicketSelected } from 'gcs-common/slices/tickets/ticketsSlice';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import TicketSearchBar from '../TicketSearchBar/TicketSearchBar';
import TicketCard from './TicketCard/TicketCard';
import styles from './styles.module.scss';

const TicketsOverview = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { routeChannelId } = useParams<{ routeChannelId: string }>();
  const channelId: string = useSelector(getSelectedChannelId);
  const ticketNotifications = useGetETSTicketNotificationsForChannelSelector(channelId);
  const { etsTicketsfilters, setETSTicketsQueryParams } = useETSTicketsQueryParams();

  useEffect(() => {
    // Reset search field when changing channels
    setETSTicketsQueryParams({ search: undefined }, false);
  }, [routeChannelId]);

  const filters = {
    ...etsTicketsfilters,
    channelId: routeChannelId,
    search: etsTicketsfilters.search,
  };

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isFetching } = useGetETSTicketsInfiniteQuery({
    query: convertETSTicketsFiltersToQuery(filters),
  });

  const tickets = data || [];

  useEffect(() => {
    queryClient
      .invalidateQueries({
        queryKey: getGetETSTicketsInfiniteQueryKey(convertETSTicketsFiltersToQuery(filters)),
      })
      .then(() => {})
      .catch(() => {});
  }, [channelId, ticketNotifications.length]);

  const navigateToForm = () => {
    dispatch(createNewTicketSelected({ channelId }));
  };

  return (
    <div className={styles.ticketsWrapper}>
      <div className={styles.header}>
        <TicketSearchBar />
        <button type="button" className={styles.plus} onClick={navigateToForm}>
          <IconComponent Icon={ICON.PLUS} color={ICON_COLOR.WHITE} size={ICON_SIZE.SMALL} />
        </button>
      </div>
      <InfiniteScroller<FetchETSTicketsResponseData['data'][number]>
        isFetchingNextPage={isFetchingNextPage}
        isFetching={isFetching}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        rowRenderer={(ticket, _index) => {
          return (
            <div className="p-1">
              <TicketCard
                id={ticket.id}
                key={ticket.id}
                status={ticket.status}
                title={ticket.title}
                createdAt={ticket.createdAt}
              />
            </div>
          );
        }}
        allRows={tickets}
        estimateSize={115}
        onEmpty={() => (
          <div>
            <p>Sie haben noch keine Tickets erstellt. Sobald Sie ein Ticket einreichen, wird es hier angezeigt.</p>
          </div>
        )}
      />
    </div>
  );
};

export default TicketsOverview;
